import type { AlphaDesignVersion } from "@nlc/shared-types";

import { AlertCircle, Loader2 } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { versionSummary } from "./versionSummary";

type VersionsPanelProps = {
  onOpen: (versionId: string) => void;
  openVersion: AlphaDesignVersion;
  versions: AlphaDesignVersion[];
};

export function VersionsPanel({
  onOpen,
  openVersion,
  versions,
}: VersionsPanelProps) {
  const ordered = [...versions].sort((a, b) => a.number - b.number);
  const openPosition = ordered.findIndex(({ id }) => id === openVersion.id) + 1;

  return (
    <section className="flex flex-col gap-2.5 border-t border-border-secondary pt-[18px]">
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-text-tertiary">
        Versions
      </h2>
      <div className="flex flex-wrap gap-2.5">
        {ordered.map((version, index) => (
          <button
            aria-current={version.id === openVersion.id ? "true" : undefined}
            aria-label={`Open version ${index + 1}`}
            className={twMerge(
              "flex size-[68px] cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-border-secondary bg-bg-secondary-subtle text-fg-quinary outline-hidden focus-visible:ring-2 focus-visible:ring-border-brand",
              version.id === openVersion.id && "border-border-brand",
              version.status === "failed" && "border-border-error",
            )}
            key={version.id}
            onClick={() => onOpen(version.id)}
            type="button"
          >
            {version.status === "generating" && (
              <Loader2 className="animate-spin" size={18} />
            )}
            {version.status === "failed" && (
              <AlertCircle className="text-fg-error-primary" size={18} />
            )}
            {version.status === "generated" && version.url && (
              <img
                alt=""
                className="size-full object-cover"
                loading="lazy"
                src={version.url}
              />
            )}
          </button>
        ))}
        <div className="flex size-16 items-center justify-center rounded-lg border-2 border-dashed border-border-secondary p-1 text-center text-xs font-semibold text-text-quaternary">
          After apply
        </div>
      </div>
      <p className="truncate text-[13px] text-text-tertiary">
        Version {openPosition}, {versionSummary(openVersion)}
      </p>
    </section>
  );
}
